import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { IngestedOrdersQueryParams } from '../api/ingested-orders-api.interface';
import { IngestedOrdersService } from './ingested-orders.service';

/**
 * Service for exporting ingested orders to CSV
 * Handles CSV download and file operations
 *
 * Architecture:
 * - Uses IngestedOrdersService for HTTP communication
 * - Provides file download and filename generation utilities
 * - Follows separation of concerns: HTTP layer vs file operations
 */
@Injectable({
  providedIn: 'root'
})
export class IngestedOrdersExportService {
  private readonly ingestedOrdersService = inject(IngestedOrdersService);

  /**
   * Export orders to CSV file
   * Delegates HTTP call to IngestedOrdersService
   *
   * @param params Query parameters (filters, search, sorting)
   * @returns Observable<Blob> containing CSV file data
   */
  exportOrders(params?: IngestedOrdersQueryParams): Observable<Blob> {
    return this.ingestedOrdersService.exportOrders(params);
  }

  /**
   * Downloads a Blob as a file using browser download API
   * Creates temporary anchor element to trigger download
   *
   * @param blob File data as Blob
   * @param filename Name for downloaded file
   */
  downloadFile(blob: Blob, filename: string): void {
    // Create blob URL
    const url = window.URL.createObjectURL(blob);

    // Create temporary anchor element
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    anchor.style.display = 'none';

    // Trigger download
    document.body.appendChild(anchor);
    anchor.click();

    // Cleanup
    document.body.removeChild(anchor);
    window.URL.revokeObjectURL(url);
  }

  /**
   * Generates filename for CSV export
   * Pattern: ordenes_ingresadas_YYYYMMDD_HHMMSS.csv
   *
   * @returns Formatted filename string
   */
  generateFilename(): string {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const seconds = String(now.getSeconds()).padStart(2, '0');

    return `ordenes_ingresadas_${year}${month}${day}_${hours}${minutes}${seconds}.csv`;
  }
}
